import React, { useState, useEffect, useMemo } from 'react';
import { usePageMeta } from '../../../hooks/usePageMeta';
import Header from '../../../components/2025/Header';
import Footer from '../../../components/2025/Footer';
import FloatingNav from '../../../components/2025/FloatingNav';
import HeroTitle from '../../../components/2025/HeroTitle';
import { Chip } from '../../../components/2025/Chip';
import ShopCard from '../../../components/2025/ShopCard';
import LoadingSpinner from '../../../components/2025/LoadingSpinner';
import { fetchShops, categorizeShops, ShopData } from '../../../services/shopService';
import Styles from './styles.module.scss';

const sectionTitles = ['ご飯/麺類', 'スイーツ', '揚げ物', '飲み物', 'その他'] as const;

type CategorizedShops = {
  'ご飯/麺類': ShopData[];
  スイーツ: ShopData[];
  揚げ物: ShopData[];
  飲み物: ShopData[];
  その他: ShopData[];
};

const Map2025: React.FC = () => {
  const initialData: CategorizedShops = useMemo(
    () => ({
      'ご飯/麺類': [],
      スイーツ: [],
      揚げ物: [],
      飲み物: [],
      その他: [],
    }),
    [],
  );

  const [categorizedShops, setCategorizedShops] = useState<CategorizedShops | null>(initialData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedShop, setSelectedShop] = useState<ShopData | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

  usePageMeta({
    title: 'フロアマップ・売店',
    description:
      'かまた祭2025のフロアマップ・売店情報。会場内の各売店の場所、メニュー、価格などをご案内します。美味しい食べ物や飲み物をお楽しみください。',
  });

  const handleSelectShop = (shop: ShopData) => {
    setSelectedShop(shop);
  };

  const handleCloseModal = () => {
    setSelectedShop(null);
  };

  useEffect(() => {
    const loadShops = async () => {
      try {
        setIsLoading(true);
        const shops = await fetchShops();
        if (shops && shops.length > 0) {
          const categorized = categorizeShops(shops);
          setCategorizedShops(categorized);
          setError(null);
        } else {
          setCategorizedShops(initialData);
          setError(null);
        }
      } catch (err) {
        console.error('ショップデータの取得に失敗しました:', err);
        setCategorizedShops(initialData);
        setError(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadShops();
  }, [initialData]);

  // モーダルの開閉に応じてbodyスクロールを制御
  useEffect(() => {
    if (selectedShop) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedShop]);

  // フィルタリングされた売店
  const filteredShops = useMemo(() => {
    if (!selectedFilter || !categorizedShops) return null;
    return categorizedShops[selectedFilter as keyof CategorizedShops] || [];
  }, [selectedFilter, categorizedShops]);

  // 全売店（ユニーク）をまとめる
  const allUniqueShops = useMemo(() => {
    if (!categorizedShops) return [] as ShopData[];
    const all = [
      ...categorizedShops['ご飯/麺類'],
      ...categorizedShops['スイーツ'],
      ...categorizedShops['揚げ物'],
      ...categorizedShops['飲み物'],
      ...categorizedShops['その他'],
    ] as ShopData[];
    const map = new Map<string, ShopData>();
    for (const s of all) {
      if (!map.has(s.id)) map.set(s.id, s);
    }
    return Array.from(map.values());
  }, [categorizedShops]);

  return (
    <div className={Styles.mapContainer}>
      <Header />
      <HeroTitle title="MAP" subtitle="フロアマップ・売店" />

      <div className={Styles.map}>
        <div className={Styles.map__bg}>
          <div className={Styles.map__bg__0}>
            <svg
              width="177"
              height="186"
              viewBox="0 0 177 186"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="-98"
                y="123.028"
                width="282.981"
                height="91.1066"
                rx="45.5533"
                transform="rotate(-29.4259 -98 123.028)"
                fill="url(#paint0_linear_2467_509)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_2467_509"
                  x1="-98"
                  y1="168.582"
                  x2="184.981"
                  y2="168.582"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#D199ED" />
                  <stop offset="1" stopColor="#CEDBFF" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div className={Styles.map__bg__1}>
            <svg
              width="115"
              height="122"
              viewBox="0 0 115 122"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="18.5191"
                cy="113.411"
                r="7.51302"
                transform="rotate(173.389 18.5191 113.411)"
                fill="url(#paint0_linear_2467_518)"
              />
              <rect
                x="164.76"
                y="15.7498"
                width="152.577"
                height="18.2606"
                rx="9.1303"
                transform="rotate(143.649 164.76 15.7498)"
                fill="url(#paint1_linear_2467_518)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_2467_518"
                  x1="11.0061"
                  y1="120.294"
                  x2="28.628"
                  y2="112.135"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#A6A8EF" />
                  <stop offset="1" stopColor="#0B48F1" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_2467_518"
                  x1="164.76"
                  y1="33.2446"
                  x2="178.368"
                  y2="-19.4031"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#A6A8EF" />
                  <stop offset="1" stopColor="#0B48F1" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div className={Styles.map__bg__2}>
            <svg
              width="68"
              height="68"
              viewBox="0 0 68 68"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M28.3494 1.52299C28.6299 -0.20106 30.9527 -0.558918 31.7391 1.00076L41.6382 20.6331C41.9899 21.3306 42.7507 21.7223 43.5228 21.6034L65.2532 18.2556C66.9796 17.9896 68.0377 20.0881 66.7974 21.318L51.1849 36.7993C50.6302 37.3494 50.4928 38.194 50.8445 38.8915L60.7435 58.5239C61.5299 60.0835 59.8611 61.7384 58.3081 60.9388L38.76 50.8744C38.0655 50.5169 37.2197 50.6472 36.665 51.1972L21.0525 66.6785C19.8122 67.9084 17.7227 66.8326 18.0032 65.1085L21.5343 43.4071C21.6597 42.6361 21.2745 41.872 20.5799 41.5144L1.03182 31.45C-0.521169 30.6504 -0.143725 28.3307 1.58264 28.0648L23.3131 24.7169C24.0851 24.598 24.6928 23.9954 24.8183 23.2244L28.3494 1.52299Z"
                fill="#F6B8DF"
              />
            </svg>
          </div>
          <div className={Styles.map__bg__3}>
            <svg
              width="140"
              height="140"
              viewBox="0 0 140 140"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="69.7208" cy="69.7208" r="69.7208" fill="#F6B8DF" />
            </svg>
          </div>

          {/* 追加の背景要素 */}
          <div className={Styles.map__bg__4}>
            <svg
              width="19"
              height="19"
              viewBox="0 0 19 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="9.5" cy="9.5" r="9.5" fill="#F6B8DF" />
            </svg>
          </div>
          <div className={Styles.map__bg__5}>
            <svg
              width="83"
              height="86"
              viewBox="0 0 83 86"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="-56"
                y="72.6885"
                width="152.577"
                height="18.2606"
                rx="9.1303"
                transform="rotate(-29.7401 -56 72.6885)"
                fill="white"
              />
            </svg>
          </div>
          <div className={Styles.map__bg__6}>
            <svg
              width="75"
              height="83"
              viewBox="0 0 75 83"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="15.5556" cy="25.2778" r="15.5556" fill="#F6B8DF" />
              <circle cx="41.5279" cy="49.3056" r="32.2222" stroke="#F6B8DF" strokeWidth="2.5" />
              <circle cx="41.5278" cy="6.52784" r="6.52778" fill="white" />
            </svg>
          </div>
          <div className={Styles.map__bg__7}>
            <svg
              width="375"
              height="482"
              viewBox="0 0 375 482"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="-149.442"
                y="457.165"
                width="853.012"
                height="37.8276"
                rx="18.9138"
                transform="rotate(-32.9591 -149.442 457.165)"
                fill="url(#paint0_linear_2467_1075)"
                fillOpacity="0.5"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_2467_1075"
                  x1="-149.442"
                  y1="476.078"
                  x2="703.569"
                  y2="476.078"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#0007D2" />
                  <stop offset="1" stopColor="white" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div className={Styles.map__bg__8}>
            <svg
              width="115"
              height="122"
              viewBox="0 0 115 122"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="18.5191"
                cy="113.411"
                r="7.51302"
                transform="rotate(173.389 18.5191 113.411)"
                fill="url(#paint0_linear_2467_518)"
              />
              <rect
                x="164.76"
                y="15.7498"
                width="152.577"
                height="18.2606"
                rx="9.1303"
                transform="rotate(143.649 164.76 15.7498)"
                fill="url(#paint1_linear_2467_518)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_2467_518"
                  x1="11.0061"
                  y1="120.294"
                  x2="28.628"
                  y2="112.135"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#A6A8EF" />
                  <stop offset="1" stopColor="#0B48F1" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_2467_518"
                  x1="164.76"
                  y1="33.2446"
                  x2="178.368"
                  y2="-19.4031"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#A6A8EF" />
                  <stop offset="1" stopColor="#0B48F1" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div className={Styles.map__bg__9}>
            <svg
              width="81"
              height="86"
              viewBox="0 0 81 86"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="-58"
                y="72.6886"
                width="152.577"
                height="18.2606"
                rx="9.1303"
                transform="rotate(-29.7401 -58 72.6886)"
                fill="url(#paint0_linear_2467_1083)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_2467_1083"
                  x1="-58"
                  y1="81.8189"
                  x2="94.5775"
                  y2="81.8189"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#0946F1" />
                  <stop offset="1" stopColor="#F1ECE6" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        <div className={Styles.container}>
          {isLoading && <LoadingSpinner />}

          {error && (
            <div className={Styles.error}>
              <p>{error}</p>
            </div>
          )}

          {!isLoading && !error && (
            <>
              {/* PDFイメージリンクセクション */}
              <div className={Styles.pdfSection}>
                <div className={Styles.pdfGrid}>
                  <div
                    className={Styles.pdfCard}
                    onClick={() => window.open('/images/2025/map/全体マップ_修正後.pdf', '_blank')}
                  >
                    <div className={Styles.pdfCard__image}>
                      <img
                        src="/images/2025/map/全体マップ_サムネイル.png"
                        alt="全体マップ"
                        className={Styles.pdfCard__thumbnail}
                      />
                    </div>
                    <h3 className={Styles.pdfCard__title}>
                      全体マップ
                      <br />
                      フロアマップ
                    </h3>
                  </div>

                  <div
                    className={Styles.pdfCard}
                    onClick={() => window.open('/images/2025/map/外売店マップ.pdf', '_blank')}
                  >
                    <div className={Styles.pdfCard__image}>
                      <img
                        src="/images/2025/map/外売店マップ_サムネイル.png"
                        alt="外売店マップ"
                        className={Styles.pdfCard__thumbnail}
                      />
                    </div>
                    <h3 className={Styles.pdfCard__title}>
                      外売店
                      <br />
                      フロアマップ
                    </h3>
                  </div>

                  <div
                    className={Styles.pdfCard}
                    onClick={() => window.open('/images/2025/map/1-2号館マップ.pdf', '_blank')}
                  >
                    <div className={Styles.pdfCard__image}>
                      <img
                        src="/images/2025/map/1-2号館_サムネイル.png"
                        alt="1-2号館マップ"
                        className={Styles.pdfCard__thumbnail}
                      />
                    </div>
                    <h3 className={Styles.pdfCard__title}>
                      1-2号館
                      <br />
                      フロアマップ
                    </h3>
                  </div>

                  <div
                    className={Styles.pdfCard}
                    onClick={() => window.open('/images/2025/map/3号館マップ.pdf', '_blank')}
                  >
                    <div className={Styles.pdfCard__image}>
                      <img
                        src="/images/2025/map/3号館_サムネイル.png"
                        alt="3号館マップ"
                        className={Styles.pdfCard__thumbnail}
                      />
                    </div>
                    <h3 className={Styles.pdfCard__title}>
                      3号館
                      <br />
                      フロアマップ
                    </h3>
                  </div>

                  <div
                    className={Styles.pdfCard}
                    onClick={() => window.open('/images/2025/map/内売店マップ.pdf', '_blank')}
                  >
                    <div className={Styles.pdfCard__image}>
                      <img
                        src="/images/2025/map/内売店_サムネイル.png"
                        alt="内売店"
                        className={Styles.pdfCard__thumbnail}
                      />
                    </div>
                    <h3 className={Styles.pdfCard__title}>
                      内売店
                      <br />
                      フロアマップ
                    </h3>
                  </div>

                  <div
                    className={Styles.pdfCard}
                    onClick={() => window.open('/images/2025/map/6号館マップ.pdf', '_blank')}
                  >
                    <div className={Styles.pdfCard__image}>
                      <img
                        src="/images/2025/map/6号館_サムネイル.png"
                        alt="6号館マップ"
                        className={Styles.pdfCard__thumbnail}
                      />
                    </div>
                    <h3 className={Styles.pdfCard__title}>
                      6号館
                      <br />
                      フロアマップ
                    </h3>
                  </div>

                  <div
                    className={Styles.pdfCard}
                    onClick={() => window.open('/images/2025/map/12号館マップ.pdf', '_blank')}
                  >
                    <div className={Styles.pdfCard__image}>
                      <img
                        src="/images/2025/map/12号館_サムネイル.png"
                        alt="12号館マップ"
                        className={Styles.pdfCard__thumbnail}
                      />
                    </div>
                    <h3 className={Styles.pdfCard__title}>
                      12号館
                      <br />
                      フロアマップ
                    </h3>
                  </div>
                </div>
              </div>

              <h2 className={Styles.shopTitle}>売店一覧</h2>
              {/* カテゴリーフィルター */}
              <div className={Styles.shopList__container}>
                {sectionTitles.map((title) => {
                  return (
                    <div
                      key={title}
                      className={`${Styles.shopList__item} ${selectedFilter && selectedFilter !== title ? Styles.shopList__itemInactive : ''}`}
                      onClick={() => {
                        if (selectedFilter === title) {
                          setSelectedFilter(null);
                        } else {
                          setSelectedFilter(title);
                        }
                      }}
                    >
                      <Chip text={title} variant="primary" />
                    </div>
                  );
                })}
                <div
                  className={`${Styles.shopList__item} ${selectedFilter ? Styles.shopList__itemInactive : ''}`}
                  onClick={() => setSelectedFilter(null)}
                >
                  <p className={Styles.shopList__viewAll}>
                    <svg
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1L6 7.08227"
                        stroke="#0946F1"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M11 1L6 7.08227"
                        stroke="#0946F1"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    全部見る
                  </p>
                </div>
              </div>

              {/* フィルタリングされた売店の表示 */}
              {selectedFilter && filteredShops && filteredShops.length > 0 && (
                <div className={Styles.filteredShops}>
                  <h2 className={Styles.filteredShops__title}>{selectedFilter}</h2>
                  <div className={Styles.filteredShops__grid}>
                    {filteredShops.map((shop) => (
                      <div key={shop.id} className={Styles.filteredShops__item}>
                        <ShopCard sectionTitle="" shops={[shop]} />
                      </div>
                    ))}
                  </div>
                  <div className={Styles.resetButtonContainer}>
                    <button className={Styles.resetButton} onClick={() => setSelectedFilter(null)}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={Styles.resetIcon}
                      >
                        <line
                          x1="2"
                          y1="2"
                          x2="18"
                          y2="18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <line
                          x1="2"
                          y1="18"
                          x2="18"
                          y2="2"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      クリア
                    </button>
                  </div>
                </div>
              )}

              {!selectedFilter && (
                <div className={Styles.sectionsContainer}>
                  {/* ご飯/麺類 */}
                  <section id="ご飯/麺類" className={Styles.section}>
                    <h2 className={Styles.section__title}>ご飯/麺類</h2>
                    {categorizedShops && categorizedShops['ご飯/麺類'].length > 0 ? (
                      <div className={Styles.categoryGrid}>
                        {categorizedShops['ご飯/麺類'].map((shop) => (
                          <div key={shop.id} className={Styles.categoryGrid__item}>
                            <ShopCard sectionTitle="" shops={[shop]} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={Styles.comingSoon}>
                        <p className={Styles.comingSoon__text}>Coming Soon...</p>
                      </div>
                    )}
                  </section>

                  {/* スイーツ */}
                  <section id="スイーツ" className={Styles.section}>
                    <h2 className={Styles.section__title}>スイーツ</h2>
                    {categorizedShops && categorizedShops['スイーツ'].length > 0 ? (
                      <div className={Styles.categoryGrid}>
                        {categorizedShops['スイーツ'].map((shop) => (
                          <div key={shop.id} className={Styles.categoryGrid__item}>
                            <ShopCard sectionTitle="" shops={[shop]} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={Styles.comingSoon}>
                        <p className={Styles.comingSoon__text}>Coming Soon...</p>
                      </div>
                    )}
                  </section>

                  {/* 揚げ物 */}
                  <section id="揚げ物" className={Styles.section}>
                    <h2 className={Styles.section__title}>揚げ物</h2>
                    {categorizedShops && categorizedShops['揚げ物'].length > 0 ? (
                      <div className={Styles.categoryGrid}>
                        {categorizedShops['揚げ物'].map((shop) => (
                          <div key={shop.id} className={Styles.categoryGrid__item}>
                            <ShopCard sectionTitle="" shops={[shop]} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={Styles.comingSoon}>
                        <p className={Styles.comingSoon__text}>Coming Soon...</p>
                      </div>
                    )}
                  </section>

                  {/* 飲み物 */}
                  <section id="飲み物" className={Styles.section}>
                    <h2 className={Styles.section__title}>飲み物</h2>
                    {categorizedShops && categorizedShops['飲み物'].length > 0 ? (
                      <div className={Styles.categoryGrid}>
                        {categorizedShops['飲み物'].map((shop) => (
                          <div key={shop.id} className={Styles.categoryGrid__item}>
                            <ShopCard sectionTitle="" shops={[shop]} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={Styles.comingSoon}>
                        <p className={Styles.comingSoon__text}>Coming Soon...</p>
                      </div>
                    )}
                  </section>

                  {/* その他 */}
                  <section id="その他" className={Styles.section}>
                    <h2 className={Styles.section__title}>その他</h2>
                    {categorizedShops && categorizedShops['その他'].length > 0 ? (
                      <div className={Styles.categoryGrid}>
                        {categorizedShops['その他'].map((shop) => (
                          <div key={shop.id} className={Styles.categoryGrid__item}>
                            <ShopCard sectionTitle="" shops={[shop]} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={Styles.comingSoon}>
                        <p className={Styles.comingSoon__text}>Coming Soon...</p>
                      </div>
                    )}
                  </section>
                </div>
              )}

              {/* 全部見る（売店） */}
              <section id="all-shops" className={Styles.allShopsSection}>
                <h2 className={Styles.allShopsSection__title}>全部見る</h2>
                <div className={Styles.allShopsSection__grid}>
                  {allUniqueShops.length > 0 ? (
                    allUniqueShops.map((shop) => (
                      <div
                        key={shop.id}
                        className={Styles.allShopsSection__item}
                        onClick={() => handleSelectShop(shop)}
                      >
                        <div className={Styles.allShopsSection__imageWrapper}>
                          <img
                            src={shop.imageUrl}
                            alt={shop.title}
                            className={Styles.allShopsSection__image}
                          />
                        </div>
                        <h3 className={Styles.allShopsSection__shopTitle}>{shop.title}</h3>
                      </div>
                    ))
                  ) : (
                    <div className={Styles.comingSoon}>
                      <p className={Styles.comingSoon__text}>Coming Soon...</p>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {/* モーダル */}
      {selectedShop && (
        <div className={Styles.modal} onClick={handleCloseModal}>
          <div className={Styles.modal__content} onClick={(e) => e.stopPropagation()}>
            <ShopCard
              sectionTitle={selectedShop.category}
              shops={[selectedShop]}
              onClose={handleCloseModal}
            />
          </div>
        </div>
      )}

      <Footer />
      <FloatingNav />
    </div>
  );
};

export default Map2025;
